import { useEffect, useState } from "react";

/**
 * Jalar hacia abajo para recargar (solo táctil). Instalada como PWA no existe el
 * botón de recargar del navegador y en iOS el gesto nativo se desactiva, así que lo
 * hacemos a mano: si la página está hasta arriba y el dedo baja más del umbral, se
 * llama `onRefresh`. Devuelve cuánto se ha jalado (px) para dibujar el indicador.
 * Si `onRefresh` devuelve una promesa, el indicador se queda girando hasta que
 * resuelva (recarga suave); si recarga la página, basta con no resolver nunca.
 *
 * Se ignora si hay un sheet abierto (body con overflow hidden), si el gesto es
 * más horizontal que vertical (chips y segmentados con scroll lateral) o si
 * empieza sobre un campo de texto.
 */
export default function usePullToRefresh(onRefresh, { threshold = 72, max = 110 } = {}) {
  const [pull, setPull] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!onRefresh) return undefined;
    let startX = null, startY = null, dist = 0, pulling = false, decided = false, frame = 0;

    // Un solo setState por frame: touchmove dispara mucho más seguido de lo que se
    // pinta y cada render de más se notaba como tirones al seguir el dedo.
    const paint = (px) => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; setPull(px); });
    };
    // Resistencia tipo liga: al principio sigue casi 1:1 al dedo y cuanto más lejos,
    // menos avanza, sin tope seco en `max` (el 0.5 lineal se sentía pesado y se
    // frenaba de golpe).
    const rubber = (dy) => max * (1 - Math.exp(-dy / (max * 1.4)));

    const canStart = (target) =>
      window.scrollY <= 0 &&
      document.body.style.overflow !== "hidden" &&
      !(target.closest && target.closest('[role="dialog"], input, textarea, select'));

    const reset = () => {
      startX = null; startY = null; dist = 0; pulling = false; decided = false;
      if (frame) { cancelAnimationFrame(frame); frame = 0; }
      setDragging(false);
    };

    const onStart = (e) => {
      if (e.touches.length !== 1 || !canStart(e.target)) { reset(); return; }
      reset();
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    const onMove = (e) => {
      if (startY == null) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      if (!decided) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        decided = true;
        if (Math.abs(dx) > Math.abs(dy) || dy < 0) { reset(); return; } // lateral o hacia arriba: no es nuestro
      }
      if (window.scrollY > 0) { reset(); setPull(0); return; }
      if (!pulling) { pulling = true; setDragging(true); }
      dist = rubber(Math.max(0, dy));
      paint(dist);
      if (e.cancelable) e.preventDefault(); // sin rebote nativo mientras jalamos
    };
    const onEnd = () => {
      if (startY == null) return;
      const fire = pulling && dist >= threshold;
      reset();
      // Al soltar ya no hay dedo que seguir: el CSS anima el regreso (sin
      // .is-dragging vuelve la transición) hacia la altura del spinner o hacia 0.
      if (fire) {
        setRefreshing(true);
        setPull(threshold * 0.75);
        Promise.resolve(onRefresh()).finally(() => { setRefreshing(false); setPull(0); });
      } else {
        setPull(0);
      }
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, [onRefresh, threshold, max]);

  return { pull, dragging, refreshing, progress: Math.min(1, pull / threshold) };
}
